import React, { useState } from 'react';
import FloatingActionButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/FloatingActionButton';
import AddIcon from 'housecall-ui/dist/IconsAndImages/Icons/Add';

import TrackedButton from 'components/Common/Inputs/Buttons/TrackedButton';
import TrackedMenuItem from 'components/Common/Navigation/Menus/TrackedMenuItem';
import { HEADER } from 'components/Services/Analytics/TrackEventsService';
import NewCustomerDialogContainer from 'components/Toolbar/Customer/NewCustomerDialogContainer';

import { useShouldDisplayCustomers, useShouldDisplayJobs } from '../displayConfig';

import NavPopover from './NavPopover';

type AddButtonProps = {
  variant?: 'button' | 'icon';
};

const AddButton = ({ variant = 'button' }: AddButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>();
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);

  const shouldDisplayCustomerItem = useShouldDisplayCustomers();
  const shouldDisplayJobItem = useShouldDisplayJobs();

  const handleClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setAnchorEl(event?.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  const isOpen = Boolean(anchorEl);
  const id = isOpen ? 'nav-add-menu' : undefined;

  const trackingProperties = { button_location: HEADER };
  const buttonVariant: { [key: string]: React.ReactElement } = {
    button: (
      <TrackedButton
        version="v2"
        trackingName="new"
        aria-describedby={id}
        aria-label="New"
        trackingProperties={trackingProperties}
        onClick={handleClick}
        color="primary"
        variant="contained"
      >
        New
      </TrackedButton>
    ),
    icon: (
      <TrackedButton
        version="v2"
        trackingName="new"
        aria-describedby={id}
        aria-label="New"
        trackingProperties={trackingProperties}
        onClick={handleClick}
        role="button"
        size="small"
        color="secondary"
        buttonComponent={FloatingActionButton}
      >
        <AddIcon />
      </TrackedButton>
    ),
  };

  const menuItems = [
    shouldDisplayCustomerItem && (
      <TrackedMenuItem
        key="new customer"
        trackingName="new customer"
        trackingProperties={trackingProperties}
        version="v2"
        onClick={() => setNewCustomerDialogOpen(true)}
      >
        Customer
      </TrackedMenuItem>
    ),
    shouldDisplayJobItem && (
      <a key="/pro/jobs/new" href="/pro/jobs/new">
        <TrackedMenuItem trackingName="new job" trackingProperties={trackingProperties} version="v2">
          Job
        </TrackedMenuItem>
      </a>
    ),
  ];

  if (menuItems.filter(Boolean).length === 0) return null;

  return (
    <>
      {buttonVariant[variant]}
      <NavPopover id={id} open={isOpen} anchorEl={anchorEl} onClose={handleClose}>
        {menuItems}
      </NavPopover>
      <NewCustomerDialogContainer
        open={newCustomerDialogOpen}
        closeDialog={() => setNewCustomerDialogOpen(false)}
        onSuccess={(_customer, newLocation) => {
          window.location = newLocation;
        }}
      />
    </>
  );
};

export default AddButton;
